import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Modal from '../Modal';
import { iFullStoreState, iExtraInfo, iList } from '../../shared/interfaces';
import styles from './AdvancedSearchModal.module.scss';

export const ADVANCED_SEARCH_PREFIX = '__advanced_search__:';

export type ComparisonOperator = 'startsWith' | 'contains' | 'greaterThan' | 'lessThan' | 'equals';

export interface AdvancedSearchCondition {
  id: string;
  fieldId: string;
  fieldLabel: string;
  operator: ComparisonOperator;
  value: string;
}

interface AdvancedSearchModalProps {
  open: boolean;
  onClose: () => void;
  onSearch: (fields: AdvancedSearchCondition[]) => void;
}

const OPERATORS: { value: ComparisonOperator; label: string }[] = [
  { value: 'startsWith', label: 'Starts with' },
  { value: 'contains', label: 'Contains' },
  { value: 'equals', label: 'Equals' },
  { value: 'greaterThan', label: 'Greater than' },
  { value: 'lessThan', label: 'Less than' },
];

export const AdvancedSearchModal: React.FC<AdvancedSearchModalProps> = ({ open, onClose, onSearch }) => {
  const [searchFields, setSearchFields] = useState<AdvancedSearchCondition[]>([
    { id: '1', fieldId: '', fieldLabel: '', operator: 'contains', value: '' },
  ]);

  const allExtraInfo = useSelector<iFullStoreState, iList<iExtraInfo>>(
    (s) => s.general.extraInfo
  );

  const extraInfoFields = Object.values(allExtraInfo || {})
    .map((info) => ({
      id: info.details.id,
      name: info.details.name,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const handleFieldChange = (id: string, fieldId: string) => {
    const fieldLabel = extraInfoFields.find((f) => f.id === fieldId)?.name || '';
    setSearchFields((prev) =>
      prev.map((field) =>
        field.id === id ? { ...field, fieldId, fieldLabel } : field
      )
    );
  };

  const handleOperatorChange = (id: string, operator: ComparisonOperator) => {
    setSearchFields((prev) =>
      prev.map((field) =>
        field.id === id ? { ...field, operator } : field
      )
    );
  };

  const handleValueChange = (id: string, value: string) => {
    setSearchFields((prev) =>
      prev.map((field) =>
        field.id === id ? { ...field, value } : field
      )
    );
  };

  const addField = () => {
    const newId = String(Math.max(...searchFields.map((f) => parseInt(f.id)), 0) + 1);
    setSearchFields((prev) => [
      ...prev,
      { id: newId, fieldId: '', fieldLabel: '', operator: 'contains', value: '' },
    ]);
  };

  const removeField = (id: string) => {
    if (searchFields.length > 1) {
      setSearchFields((prev) => prev.filter((field) => field.id !== id));
    }
  };

  const handleSearch = () => {
    const validFields = searchFields.filter((f) => f.fieldId && f.value?.trim());
    if (validFields.length > 0) {
      onSearch(validFields);
      onClose();
    }
  };

  const handleReset = () => {
    setSearchFields([
      { id: '1', fieldId: '', fieldLabel: '', operator: 'contains', value: '' },
    ]);
  };

  if (!open) return null;

  return (
    <Modal onBackdropClick={onClose}>
      <div className={styles.advancedSearchContainer}>
        <h2 className={styles.title}>Advanced Search</h2>

        <div className={styles.fieldsContainer}>
          {searchFields.map((field) => (
            <div key={field.id} className={styles.searchRow}>
              <select
                className={styles.fieldSelect}
                value={field.fieldId}
                onChange={(e) => handleFieldChange(field.id, e.target.value)}
              >
                <option value="">Select Field</option>
                {extraInfoFields.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>

              <select
                className={styles.operatorSelect}
                value={field.operator}
                onChange={(e) =>
                  handleOperatorChange(field.id, e.target.value as ComparisonOperator)
                }
              >
                {OPERATORS.map((op) => (
                  <option key={op.value} value={op.value}>
                    {op.label}
                  </option>
                ))}
              </select>

              <input
                type="text"
                className={styles.valueInput}
                placeholder="Value"
                value={field.value}
                onChange={(e) => handleValueChange(field.id, e.target.value)}
              />

              <button
                type="button"
                className={styles.removeBtn}
                onClick={() => removeField(field.id)}
                disabled={searchFields.length === 1}
                title="Remove field"
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <button type="button" className={styles.addFieldBtn} onClick={addField}>
          + Add Field
        </button>

        <div className={styles.actions}>
          <button type="button" className={styles.cancelBtn} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={styles.resetBtn} onClick={handleReset}>
            Reset
          </button>
          <button type="button" className={styles.searchBtn} onClick={handleSearch}>
            Search
          </button>
        </div>
      </div>
    </Modal>
  );
};
